import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `wallet` domain.
 *
 * Two facts drive nearly all of this copy, both read out of
 * `apps/wallet/src/repo/ledger.ts` and `apps/wallet/src/routes/wallets.ts`:
 *
 *   - `transactions.balance_after` is the balance of the ONE POOL the row
 *     touched, so the ledger's Previous/New Balance columns cannot be
 *     reconciled against the Balance tile and consecutive rows do not chain
 *   - the manual adjustment posts with no idempotency key and
 *     `allowNegative` on subtract, so a double submit moves money twice and the
 *     platform's compensation endpoint — which is addressed by idempotency key
 *     — can never reach the row it created
 */
export const walletGuide: DomainGuide = {
  domain: 'wallet',
  pages: [
    {
      id: 'wallet.page',
      route: '/wallet',
      permission: 'wallet:read',
      status: 'live',
      fields: [
        { key: 'creditCash', kind: 'money-unsigned' },
        { key: 'creditPromotion', kind: 'money-unsigned' },
        {
          key: 'balance',
          kind: 'money-unsigned',
          formula: 'balance = cash + promotion + commission + cashback',
        },
        {
          key: 'withdrawable',
          kind: 'money-unsigned',
          formula: 'withdrawable = cash   (commission must be transferred to cash first)',
        },
        {
          key: 'previousBalance',
          kind: 'money-unsigned',
          formula: 'previousBalance = newBalance − amount   (one pool only)',
        },
        { key: 'cashDelta', kind: 'money-signed' },
        { key: 'bonusDelta', kind: 'money-signed' },
        { key: 'newBalance', kind: 'money-unsigned' },
        { key: 'txStatus', kind: 'status' },
        { key: 'pendingAmount', kind: 'money-unsigned' },
        { key: 'pendingStatus', kind: 'status' },
      ],
      cautionCount: 7,
      warningCount: 0,
    },
    {
      id: 'wallet.adjust',
      route: '/wallet (adjust dialog)',
      permission: 'wallet:adjust',
      status: 'live',
      fields: [
        { key: 'op', kind: 'text' },
        { key: 'pool', kind: 'text' },
        { key: 'amount', kind: 'money-unsigned' },
        { key: 'reason', kind: 'text' },
        { key: 'bankAccount', kind: 'text' },
        { key: 'promotionCondition', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 2,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
